// imports
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

import utc from 'dayjs/plugin/utc';
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

// lib files
import { Channel, Theme } from '@/lib/types/api/multi-livestream';
import { getScheduleTimes } from '@/lib/helpers/multi-livestream/getScheduleTimes';
import useDidMount from '@/lib/hooks/useDidMount';

// components
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import MultiLivestreamChannel from '@/components/MultiLivestream/Schedule/Channel';
import MultiLivestreamTimeline from '@/components/MultiLivestream/Schedule/Timeline';

// styles
import styles from '@/components/MultiLivestream/Schedule/Schedule.module.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

type MultiLivestreamScheduleProps = {
  selectedChannel: string;
  setUserSelectedChannel: Dispatch<SetStateAction<string>>;
  setDataFetchError: Dispatch<SetStateAction<boolean>>;
  stationId: string;
  primaryTimezone: string;
  theme: Theme;
};

const MultiLivestreamSchedule = (
  props: MultiLivestreamScheduleProps
): ReactElement => {
  const {
    selectedChannel,
    setUserSelectedChannel,
    setDataFetchError,
    stationId,
    primaryTimezone,
    theme,
  } = props;

  const scheduleWrapperRef = useRef<HTMLDivElement>(null);
  const scheduleRef = useRef<HTMLDivElement>(null);
  const listingsRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line
  let nowOverride: Dayjs | null = null;
  // nowOverride is a developer convenience to stress test schedules,
  // - it allows you to dictate what "now" is on the schedule.
  // You need to set it to
  // - today's date (note timezone - it may need to be EST or EDT depending
  // on the time of year)
  // - sometime after the start of the current half-hour schedule window
  // - it can be several hours into the future, if needed

  // !!!! comment it out before commiting !!!
  // nowOverride = dayjs('Fri, 10 Oct 2025 13:35:07 EDT');

  // The "now" state is very important, and drives the rest of the schedule
  const [now, setNow] = useState(nowOverride ? nowOverride : dayjs().tz(primaryTimezone));

  // get today's date in YYYY-MM-DD format
  const today = now.format('YYYY-MM-DD');
  const [scheduleDate, setScheduleDate] = useState<string>(today);
  const [scheduleTimes, setScheduleTimes] = useState<Dayjs[]>([]);
  const [scheduleData, setScheduleData] = useState<Channel[]>();

  const fetchScheduleData = useCallback(async () => {
    try {
      const response = await fetch(`/api/station/${stationId}/livestream/?date=${scheduleDate}`);
      const json = await response.json();
      setScheduleData(json.schedule.content[0]?.channels)
    } catch(error) {
      console.log({ error })
      setDataFetchError(true)
    }
  }, [stationId, setDataFetchError, scheduleDate])

  useDidMount(fetchScheduleData);

  // we update "now" every 1 minute
  // note - you can change 60 to another value, like 5,
  // to update every 5 seconds during development, which is helpful
  // when paired with nowOverride - this will essentially let you
  // 'fast forward' in time
  // !!!! set this to "60 * 1000" before commiting !!!
  const NOW_INTERVAL = 60 * 1000;

  // calcuting how many minutes are left in the day's schedule
  const minutesLeftInDaySchedule =
    // 24 hours in minutes (24 * 60 = 1440)
    1440
    // minus the hour we're at in the primary timezone expressed as minutes
    - (now.tz(primaryTimezone).hour() * 60)
    // if we're at or past the :30 minute mark, we need to subtract
    // an additional 30 minutes
    - (now.tz(primaryTimezone).minute() >= 30 ? 30 : 0);

  const [minutesInSchedule, setMinutesInSchedule] = useState(minutesLeftInDaySchedule);

  useEffect(() => {
    const interval = setInterval(() => {
      const nowIntervalisHacked = NOW_INTERVAL !== 60 * 1000;
      setNow(nowIntervalisHacked || nowOverride ? now.add(1, 'minute') : dayjs().tz(primaryTimezone));
    }, NOW_INTERVAL);
    return () => clearInterval(interval);
  }, [now, nowOverride, NOW_INTERVAL, primaryTimezone]);

  // update on initial load and repeat every minute:
  // - schedule times
  // - percent complete
  // - minutes in schedule
  useEffect(() => {
    if (primaryTimezone) {
      setScheduleTimes(
        getScheduleTimes(now, primaryTimezone, scheduleDate)
      );
      if (today === scheduleDate) {
        setMinutesInSchedule(minutesLeftInDaySchedule);
      } else {
        setMinutesInSchedule(24 * 60);
      }
    }
  }, [
    minutesLeftInDaySchedule,
    now,
    primaryTimezone,
    scheduleDate,
    today,
  ]);

  useEffect(() => {
    setScheduleData(undefined);
    fetchScheduleData();
  }, [fetchScheduleData, scheduleDate]);

  useEffect(() => {
    // RWEB-9484 if we've switched to another date, and it's not today,
    // and we have loaded scheduleData, then we want to trigger
    // a scroll of the schedule to the current day's time
    if (today !== scheduleDate && scheduleRef.current && listingsRef.current) {
      const scheduleWidth = scheduleRef.current.offsetWidth;
      const listingsWidth = listingsRef.current.offsetWidth;
      // We need to know the difference between the overall schedule
      // and a collection of listings. That difference is the
      // button column for switching channels
      const diff = scheduleWidth - listingsWidth;
      // What percentage of the day has gone by (rounded to the previous half hour)?
      const timePercentage = (now.hour() * 60 + (now.minute() >= 30 ? 30 : 0)) / (24 * 60);
      // We then need to scroll the wrapper to: the schedule minus the diff, times that percentage
      const scrollTo = (scheduleWidth - diff) * timePercentage;
      scheduleWrapperRef.current?.scrollTo({ left: (scrollTo), behavior: 'auto' });
    }
  // We *only* want this to run when scheduleData changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleData]);

  return (
    <section className={styles.multi_livestream__schedule__container} data-sentry-component="MultiLivestreamSchedule" data-sentry-source-file="Schedule.tsx">
      <div className={styles.multi_livestream__schedule__wrapper} ref={scheduleWrapperRef}>
        {selectedChannel && scheduleData && scheduleTimes.length > 0 ? (
          <div
            className={styles.multi_livestream__schedule}
            ref={scheduleRef}
            style={{
              gridTemplateColumns: `clamp(90px, 30vw + 1rem, 200px) repeat(${minutesInSchedule}, 1fr)`,
              width: `clamp(${minutesInSchedule * 5}px, ${minutesInSchedule}vw + 1em, ${minutesInSchedule * 15}px)`
            }}
          >
            <>
              <MultiLivestreamTimeline
                handleScheduleDateChange={(date) => setScheduleDate(date)}
                now={now}
                scheduleTimes={scheduleTimes}
                scheduleDate={scheduleDate}
              />
              {/* Channels */}
              {scheduleData.map((channel: Channel, channelIndex) => {
                return (
                  <MultiLivestreamChannel
                    channel={channel}
                    key={channelIndex}
                    setUserSelectedChannel={setUserSelectedChannel}
                    selectedChannel={selectedChannel}
                    scheduleTimes={scheduleTimes}
                    now={now}
                    theme={theme}
                    ref={listingsRef}
                  />
                );
              })}
            </>
          </div>
        ) : (
          <div className={styles.multi_livestream__schedule__loading}>
            <LoadingSpinner
              text="Loading Live TV Schedule..."
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default MultiLivestreamSchedule;
